import {
    ConsentLevel,
    ScriptLoadResult,
    useFeature,
    useScriptWithConsent,
} from '@news-mono/web-common'
import React from 'react'
import { LoggerContext } from '../../../diagnostics/LoggerContext'
import { ConsentFallback } from '../../../tracking/ConsentFallback/ConsentFallback'
import { TextLink } from '../../../typography/TextLink/TextLink'
import { useSectionMetaInfo } from '../../../__helpers/use-section-meta'
import { determineApiRoute, initFb } from '../helpers/facebook'
import { OEmbedResult } from '../helpers/oembed'

export interface FacebookEmbedProps {
    link: string
}

export const FacebookEmbed: React.FC<FacebookEmbedProps> = ({ link }) => {
    const { FacebookAppId, FacebookClientToken } = useSectionMetaInfo()
    const isOembedPageEnabled = useFeature('enable-meta-oembed_page', true)
    const logger = React.useContext(LoggerContext)
    const [fbHtml, setFbHtml] = React.useState('')

    const kind = determineApiRoute(link)
    let isValidEmbedLink = true

    // `oembed_page` is deprecated, and will be removed from 1 October 2025
    // See https://developers.facebook.com/blog/post/2025/04/08/oembed-updates/
    if (kind === 'oembed_page' && !isOembedPageEnabled) {
        isValidEmbedLink = false
    }

    const [result, error] = useScriptWithConsent({
        consentMet: {
            src: 'https://connect.facebook.net/en_US/sdk.js',
        },
        consentRequiredLevel: ConsentLevel.All,
    })

    React.useEffect(() => {
        async function getFacebookOembed() {
            try {
                const fbUrl = encodeURIComponent(link)
                /**
                 * Access token generated from appid / clienttoken => {app-id}|{client-token}
                 * https://developers.facebook.com/docs/plugins/oembed
                 */
                const accessToken = `${FacebookAppId}|${FacebookClientToken}`

                if (!isValidEmbedLink) {
                    return
                }

                // New Oembed api - omit the script as we load it via useScriptWithConsent
                const graphRequest = await fetch(
                    `https://graph.facebook.com/v23.0/${kind}?omitscript=true&url=${fbUrl}&access_token=${accessToken}`,
                )

                const response = (await graphRequest.json()) as OEmbedResult
                const html = response.html

                if (html) {
                    setFbHtml(html)
                    initFb(FacebookAppId)
                }
            } catch (error) {
                logger.debug({ error, link }, 'Error loading facebook post')
            }
        }

        if (result === ScriptLoadResult.Success) {
            getFacebookOembed()
        }
    }, [
        result,
        FacebookClientToken,
        FacebookAppId,
        link,
        logger,
        isOembedPageEnabled,
        kind,
        isValidEmbedLink,
    ])

    if (result === ScriptLoadResult.NotConsented) {
        return <ConsentFallback />
    }

    if (!isValidEmbedLink || result === ScriptLoadResult.Failed) {
        return (
            <TextLink href={link} openInNewWindow={true}>
                View Facebook Embed
            </TextLink>
        )
    }

    if (!fbHtml || result === ScriptLoadResult.Loading) {
        return (
            <TextLink href={link} openInNewWindow={true}>
                Loading Facebook Embed
            </TextLink>
        )
    }

    if (fbHtml) {
        return (
            <div
                dangerouslySetInnerHTML={{
                    __html: fbHtml,
                }}
            />
        )
    }

    return null
}

export default FacebookEmbed
